import React, { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, makeStyles, MenuItem, Select } from '@material-ui/core'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost';
import { sortBy, prop, replace, omit, assoc, pipe, evolve, isNil } from 'ramda'
import { Column, EditComponentProps } from 'material-table'

import { withErrorNotification } from 'components/hocs/with-error-notification'
import SEO from 'components/seo'
import { Table } from 'components/table'
import { uploadFile } from 'services/upload'

const useStyles = makeStyles({
  img: {
    width: 50,
    height: 'auto',
  },
});

interface Logo {
  url: string;
  id: number;
  name: string;
}

export enum SponsorType {
  Sponsor = 'sponsor',
  Patron = 'patron',
  Information = 'information',
  Friend = 'friend',
}
interface Sponsor {
  id: string;
  name: string;
  link: string;
  type: SponsorType;
  logo: Logo;
  order: number;
  is_visible: boolean;
  __typename: 'Sponsor';
}

type SponsorData = Omit<Sponsor, 'logo'> & {
  logo: Logo | File;
}

type Sponsors = Array<Sponsor>

interface Data { sponsors: Sponsors };

const EditSponsorsTemplate = ({ setError }) => {
  const { t } = useTranslation()

  const classes = useStyles()
  const STORAGE_PATH = process.env.GATSBY_API_URL

  const { data, refetch, loading } = useQuery<Data>(SPONSORS)
  const [createSponsor] = useMutation(CREATE_SPONSOR)
  const [updateSponsor] = useMutation(UPDATE_SPONSOR)
  const [deleteSponsor] = useMutation(DELETE_SPONSOR)

  const tableData = data && sortBy(prop('order'), data.sponsors)
  const renderLogo = ({ name, logo }: SponsorData) => (
    <>
      {
        logo && (logo as Logo).url &&
        <img className={classes.img} src={STORAGE_PATH + (logo as Logo).url} alt={name} />
      }
    </>
  )

  const renderLink = ({ link }: SponsorData) => (
    <a href={link} target='_blank'>
      <Typography>{link}</Typography>
    </a>
  )


  const renderFileInput = ({ rowData: { logo }, onChange }: EditComponentProps<SponsorData>) => (
    <>
      {
        logo && logo.name &&
        <Typography>{logo.name}</Typography>
      }
      <input
        type='file'
        accept='image/*'
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          // @ts-ignore
          !!e && onChange(e.target.files[0]);
        }}
      />
    </>
  )

  const getOrder = () => {
    if (tableData && tableData.length) {
      return (tableData.length + 1)
    }
    return 1;
  }
  const translations = {
    [SponsorType.Sponsor]: t('admin.sponsor-type-sponsor'),
    [SponsorType.Patron]: t('admin.sponsor-type-patron'),
    [SponsorType.Information]: t('admin.sponsor-type-information'),
    [SponsorType.Friend]: t('admin.sponsor-type-friend'),
  };

  const columns: Array<Column<SponsorData>> = [
    { title: t('admin.name'), field: 'name' },
    { title: t('admin.link'), field: 'link', render: renderLink },
    { title: t('admin.is-visible'), field: 'is_visible', type: 'boolean' },
    {
      title: t('admin.sponsor-type'),
      field: 'type',
      render: ({ type }) => {
        return translations[type];

      },
      editComponent: ({ rowData: { type }, onChange }) => {
        return <Select
          defaultValue={type}
          value={type as SponsorType}
          variant='filled'
          onChange={(e: ChangeEvent<{ name?: string, value: unknown }>) => {
            onChange(e.target.value)
          }}
          inputProps={{
            id: 'type',
          }}
        >
          <MenuItem value='' />
          {
            Object.entries(translations).map(([value, label]) =>
              <MenuItem key={value} value={value}>{translations[value]}</MenuItem>)
          }
        </Select>
      }
    },
    {
      title: t('admin.logo'),
      field: 'logo',
      render: renderLogo,
      editComponent: renderFileInput,
    },
  ]

  const handleAdd = async (sponsor: SponsorData) => {
    const response = await uploadFile(sponsor.logo as File)
    if (typeof response !== 'number') {
      setError(response.response)
      throw new Error('upload error')
    } else {
      const variables = pipe(
        omit(['id', 'logo', '__typename']),
        assoc('logo', response),
        assoc('order', getOrder()),
      )(sponsor)
      if (isNil(variables.is_visible)) {
        variables.is_visible = false;
      }
      await createSponsor({ variables })
      refetch()
    }
  }

  const handleUpdate = async (sponsor: SponsorData) => {
    let transform = {}

    if (sponsor.logo instanceof File) {
      const response = await uploadFile(sponsor.logo as File)
      if (typeof response !== 'number') {
        setError(response.response)
        throw new Error('upload error')
      } else {
        transform = {
          logo: () => response,
        }
      }
    } else {
      transform = {
        logo: (logo: Logo) => logo.id,
      }
    }


    const data = pipe(
      omit(['__typename']),
      evolve(transform),
    )(sponsor)

    await updateSponsor({ variables: data })
    refetch()
  }

  const handleDelete = async (oldData: SponsorData) => {
    await deleteSponsor({ variables: { id: oldData.id } })
    refetch()
  }

  return (
    <>
      <SEO title={t('page.sponsors')} />
      <Table
        isLoading={loading}
        title={t('page.sponsors')}
        addItemLabel={t('admin.add-sponsor')}
        columns={columns}
        data={tableData}

        editable={{
          onRowAdd: handleAdd,
          onRowUpdate: handleUpdate,
          onRowDelete: handleDelete,
        }}
      />
    </>
  )
}

export const EditSponsors = withErrorNotification(EditSponsorsTemplate)

const SPONSOR_MODEL = `
          $name: String,
          $link: String,
          $is_visible: Boolean!,
          $logo: ID!,
          $order: Int!
          $type: ENUM_SPONSOR_TYPE!
          `
const UPDATE_SPONSOR_MODEL = replace('/!/g', '', SPONSOR_MODEL)

const SPONSORS = gql`
          {
            sponsors {id, name, link, order, logo {url, id, name}, is_visible, type }
}`;

const CREATE_SPONSOR = gql`
          mutation CreateSponsor (${SPONSOR_MODEL}) {
            createSponsor(input: {
            data: {
            name: $name,
          link: $link,
          is_visible: $is_visible,
          logo: $logo,
          order: $order
          type: $type
      }
    }) {
            sponsor {name}
    }
  }
          `;

const UPDATE_SPONSOR = gql`
          mutation UpdateSponsor ($id: ID!, ${UPDATE_SPONSOR_MODEL}) {
            updateSponsor(input: {
            data: {
            name: $name,
          link: $link,
          is_visible: $is_visible,
          logo: $logo,
          order: $order
          type: $type
      },
          where: {id: $id }
    }) {
            sponsor {name}
    }
  }
          `;

const DELETE_SPONSOR = gql`
          mutation DeleteSponsor ($id: ID!) {
            deleteSponsor(input: {where: {id: $id } }) {
            sponsor {id}
    }
  }
          `;
